import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import baseURL from '../baseURL';
import GirviPageShell from '../components/common/GirviPageShell';

const BUSINESS_NAME = 'JINESH FINANCE PAWN BROKER.';
const BUSINESS_ADDRESS = '17(2)/XI, FLOWER STREET, BELLARY';

const ProfitLossStatement = () => {
  const [user, setUser] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchProfitLoss = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [plRes, detRes] = await Promise.all([
        fetch(`${baseURL}/accounting-entries/profit-loss?${params.toString()}`, { headers }),
        fetch(`${baseURL}/accounting-entries/profit-loss-detailed?${params.toString()}`, { headers })
      ]);

      if (!plRes.ok) {
        const errorText = await plRes.text();
        console.error('🔍 Error response:', errorText);
        throw new Error(`HTTP error! status: ${plRes.status}`);
      }

      const result = await plRes.json();
      const detailed = detRes.ok ? await detRes.json() : { data: {} };

      setData({
        ...result.data,
        rows: detailed.data?.rows || [],
        totalDebit: detailed.data?.totalDebit ?? result.data?.totalDebit,
        totalCredit: detailed.data?.totalCredit ?? result.data?.totalCredit
      });
    } catch (err) {
      console.error('🔍 Error fetching profit & loss:', err);
      setError('Failed to fetch profit & loss statement.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Set default date range to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setFrom(firstDay.toISOString().split('T')[0]);
    setTo(lastDay.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (from && to) {
      fetchProfitLoss();
    }
  }, [from, to]);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const profileRes = await fetch(baseURL + '/auth/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const profile = await profileRes.json();
        setUser(profile.data?.user || null);
      } catch {
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount) => {
    if (amount === '' || amount == null) return '';
    const n = Number(amount);
    if (Number.isNaN(n)) return '';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-GB');
  };

  // Ledger column widths (in characters)
  const COLS = [12, 10, 10, 18, 40, 14, 14];
  const HEADER = ['DATE', 'DOCUMENT', 'A/C CODE', 'A/C NAME', 'DETAILS', 'DEBIT AMOUNT', 'CREDIT AMOUNT'];

  // Helper to pad/align text
  const pad = (str, len, align = 'left') => {
    str = str == null ? '' : String(str);
    if (str.length > len) return str.slice(0, len);
    if (align === 'right') return ' '.repeat(len - str.length) + str;
    return str + ' '.repeat(len - str.length);
  };

  // Use the correct data structure from the backend
  const revenueItems = data?.revenue?.items || [];
  const expenseItems = data?.expenses?.items || [];
  const totalRevenue = data?.revenue?.total || 0;
  const totalExpenses = data?.expenses?.total || 0;
  const netProfit = data?.netProfit || 0;
  
  // For backward compatibility, create rows array if needed
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const totalDebit = data?.totalDebit || 0;
  const totalCredit = data?.totalCredit || 0;
  
  // Calculate total interest earned (credit entries with interest-related descriptions or RE001 account)
  const totalInterestEarned = rows.reduce((sum, row) => {
    const details = (row.details || '').toLowerCase();
    const acct = (row.accountName || '').toLowerCase();
    const isInterestEntry = row.credit > 0 && (
      row.accountCode === 'RE001' || 
      details.includes('interest') ||
      acct.includes('interest')
    );
    return sum + (isInterestEntry ? row.credit : 0);
  }, 0);

  // Calculate capital account movements (LI003 account)
  const capitalMovements = rows.reduce((sum, row) => {
    const isCapitalEntry = row.accountCode === 'LI003' || 
                          (row.accountName || '').toLowerCase().includes('capital');
    if (isCapitalEntry) {
      // For capital account: Credit = Capital used (decrease), Debit = Capital freed (increase)
      return sum + (row.debit || 0) - (row.credit || 0);
    }
    return sum;
  }, 0);

  // --- GROUPING LOGIC START ---
  // Group rows by groupCode, then by accountName (ledger)
  const groupByLedger = (arr) => {
    const groupMap = {};
    arr.forEach(row => {
      const group = row.groupCode || 'NO_CODE';
      if (!groupMap[group]) groupMap[group] = {};
      const ledger = row.accountName || 'NO_LEDGER';
      if (!groupMap[group][ledger]) groupMap[group][ledger] = { debit: 0, credit: 0, groupCode: group, accountName: ledger };
      groupMap[group][ledger].debit += row.debit || 0;
      groupMap[group][ledger].credit += row.credit || 0;
    });
    return groupMap;
  };

  // Only consider group codes 1-600 for P&L, robust to string/number/leading zeros
  const parseGroupCode = (groupCode) => {
    if (!groupCode) return null;
    if (typeof groupCode === 'number') return groupCode;
    if (typeof groupCode === 'string') {
      // Remove leading zeros and parse
      const num = parseInt(groupCode.replace(/^0+/, ''), 10);
      return isNaN(num) ? null : num;
    }
    return null;
  };
  const isIncome = (groupCode) => {
    const code = parseGroupCode(groupCode);
    return code !== null && code >= 1 && code <= 300;
  };
  const isExpense = (groupCode) => {
    const code = parseGroupCode(groupCode);
    return code !== null && code >= 301 && code <= 600;
  };

  // Group and aggregate
  const groupedLedgers = groupByLedger(rows);
  const incomeGroups = Object.keys(groupedLedgers).filter(isIncome).sort();
  const expenseGroups = Object.keys(groupedLedgers).filter(isExpense).sort();

  // Debug logs (must come after groupedLedgers is defined)
  console.log('🔍 Rows:', rows);
  console.log('🔍 Grouped Ledgers:', groupedLedgers);
  console.log('🔍 Income Groups:', incomeGroups);
  console.log('🔍 Expense Groups:', expenseGroups);
  console.log('🔍 Data structure:', data);
  console.log('🔍 Revenue data:', data?.revenue);
  console.log('🔍 Expenses data:', data?.expenses);

  // Helper to sum amounts for a group
  const sumAmount = (obj, type) => Object.values(obj).reduce((sum, row) => sum + (type === 'credit' ? row.credit : row.debit), 0);

  // --- GROUPING LOGIC END ---

  // --- RENDER REVENUE/EXPENSE TABLE (SUMMARY) ---
  const renderItemsTable = (items, type) => (
    <TableContainer>
      <Table size="small" sx={{ minWidth: 600, fontFamily: 'monospace' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Ledger Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Amount (₹)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={2} sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                No {type === 'credit' ? 'revenue' : 'expense'} entries found for this period
              </TableCell>
            </TableRow>
          ) : (
            items.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell>{item.name}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  ₹{formatCurrency(item.amount)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Use the totals from the backend response
  const totalIncome = totalRevenue;
  const totalExpense = totalExpenses;

  // Build the ledger lines
  const ledgerLines = [];
  // Header row
  ledgerLines.push(
    HEADER.map((h, i) => pad(h, COLS[i], i >= 5 ? 'right' : 'left')).join('  ')
  );
  ledgerLines.push('-'.repeat(COLS.reduce((a, b) => a + b, 0) + 12));
  // Data rows
  rows.forEach(row => {
    const detailsStr = (row.details || '').toLowerCase();
    const acctStr = (row.accountName || '').toLowerCase();
    const isInterestEntry = row.credit > 0 && (
      row.accountCode === 'RE001' || 
      detailsStr.includes('interest') ||
      acctStr.includes('interest')
    );
    
    const isCapitalEntry = row.accountCode === 'LI003' || 
                          acctStr.includes('capital');
    
    // Add prefixes for special entries
    let accountName = row.accountName;
    if (isInterestEntry) {
      accountName = `*${accountName}`;
    } else if (isCapitalEntry) {
      accountName = `#${accountName}`;
    }
    
    ledgerLines.push([
      pad(formatDate(row.date), COLS[0]),
      pad(row.document, COLS[1]),
      pad(row.accountCode, COLS[2]),
      pad(accountName, COLS[3]),
      pad(row.details || '', COLS[4]),
      pad(formatCurrency(row.debit), COLS[5], 'right'),
      pad(formatCurrency(row.credit), COLS[6], 'right'),
    ].join('  '));
  });
  // Totals row
  ledgerLines.push('-'.repeat(COLS.reduce((a, b) => a + b, 0) + 12));
  ledgerLines.push(
    pad('TOTAL', COLS[0] + COLS[1] + COLS[2] + COLS[3] + COLS[4], 'right') +
    '  ' +
    pad(formatCurrency(totalDebit), COLS[5], 'right') +
    '  ' +
    pad(formatCurrency(totalCredit), COLS[6], 'right')
  );

  return (
    <GirviPageShell
      title="Profit & Loss"
      subtitle="Transactions · Statements"
      chipLabel="Transactions"
      maxWidth="xl"
    >
      {user && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>{user.name}</h2>
          <div>{user.companyName}</div>
          <div>{user.address}</div>
          <div>Mobile: {user.mobileNumber}</div>
          <div>License: {user.licenseNumber}</div>
        </div>
      )}
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 2, fontFamily: 'monospace' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>{BUSINESS_NAME}</Typography>
        <Typography variant="body2">{BUSINESS_ADDRESS}</Typography>
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
          PROFIT & LOSS STATEMENT FROM {from} TO {to}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>PAGE NO : 1</Typography>
      </Box>

      {/* Date Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
        <TextField
          label="From Date"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="To Date"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <Button
          variant="contained"
          onClick={fetchProfitLoss}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Generate Report
        </Button>
        <Button
          variant="outlined"
          onClick={handlePrint}
          startIcon={<PrintIcon />}
        >
          Print
        </Button>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Interest and Capital Summary */}
      <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, border: '1px solid #ddd' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center' }}>
          LEDGER SUMMARY
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#666' }}>
              INTEREST LEDGER
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.2em' }}>
              ₹{formatCurrency(totalInterestEarned)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#666' }}>
              CAPITAL ACCOUNT
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1.2em', color: capitalMovements >= 0 ? '#2e7d32' : '#d32f2f' }}>
              {capitalMovements === 0 ? 'No Capital Movements' : `₹${formatCurrency(Math.abs(capitalMovements))} ${capitalMovements >= 0 ? '(Freed)' : '(Used)'}`}
            </Typography>
          </Box>
        </Box>
        
        {/* Pledge Accounting Flow Explanation */}
        <Box sx={{ mt: 2, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1, fontSize: '0.9em' }}>
          <Typography variant="body2" sx={{ textAlign: 'center', color: '#1565c0' }}>
            <strong>Pledge Accounting Flow:</strong> When pledge created → Capital DEBIT (used) | When pledge released → Capital CREDIT (freed)
          </Typography>
          {capitalMovements === 0 && (
            <Typography variant="body2" sx={{ textAlign: 'center', color: '#f57c00', mt: 1 }}>
              <strong>Note:</strong> No capital movements found. This may be because capital accounting was recently added to the system.
            </Typography>
          )}
        </Box>
      </Box>

      {totalRevenue === 0 && totalExpenses === 0 && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff8e1', borderRadius: 1, border: '1px solid #ffcc80' }}>
          <Typography variant="body2">
            <strong>Why is this empty?</strong> Creating a pledge only moves money between balance sheet accounts (e.g. Sundry Pledge ↑, Cash ↓). 
            That does not create income or expense. Revenue and expenses appear here when you record items such as interest received, salaries, or other income/expense transactions.
          </Typography>
        </Box>
      )}

      {/* Revenue Table */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'green', mb: 1 }}>
          REVENUE
        </Typography>
        {renderItemsTable(revenueItems, 'credit')}
        <Box sx={{ textAlign: 'right', mt: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'green' }}>
            Total Revenue: ₹{formatCurrency(totalRevenue)}
          </Typography>
        </Box>
      </Box>
      {/* Expense Table */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'red', mb: 1 }}>
          EXPENSES
        </Typography>
        {renderItemsTable(expenseItems, 'debit')}
        <Box sx={{ textAlign: 'right', mt: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'red' }}>
            Total Expenses: ₹{formatCurrency(totalExpenses)}
          </Typography>
        </Box>
      </Box>
      {/* Net Profit */}
      <Box sx={{ textAlign: 'right', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: netProfit >= 0 ? 'green' : 'red' }}>
          Net Profit: ₹{formatCurrency(netProfit)}
        </Typography>
      </Box>
      
      {/* Legend */}
      <Box sx={{ mt: 2, p: 1, backgroundColor: '#f9f9f9', borderRadius: 1, fontSize: '0.9em' }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          * = Interest Income Entries | # = Capital Account Entries
        </Typography>
      </Box>
    </GirviPageShell>
  );
};

export default ProfitLossStatement; 